import { useCallback, useEffect, useRef, useState } from 'react'

// One shared read for every canvas panel. Two keys do separate jobs:
//
// `identity` is what the loaded copy is of *right now*, revision mark included, so it is
// also the refetch trigger: when the mark moves, the identity moves and a fresh read runs.
// `retainKey` is the stable key of the artifact itself, revision mark left out.
//
// While a refetch for the same retainKey is in flight, `artifact` keeps returning the
// previous copy instead of collapsing to null. Taking what a reader is reading off the
// screen to read it again unmounts the control they just used, drops focus to the body,
// and remounts the notice their warnings arrive in; a refetch of the same artifact is not
// a reason any of that should happen. A different retainKey clears immediately: a new
// artifact's loading state must never be papered over with the artifact just left.
export function usePanelArtifact<T>(opts: {
  identity: string
  retainKey: string
  enabled: boolean
  load: (signal: AbortSignal) => Promise<T>
}): {
  artifact: T | null
  artifactAttempt: number
  artifactVersion: number
  failed: boolean
  reload: () => number
} {
  const { enabled, identity, load, retainKey } = opts
  // The caller builds its load closure inline, so its identity changes every render. The
  // latest one is what each attempt calls, while the attempt itself is keyed by the two
  // strings plus the reload counter alone.
  const loadRef = useRef(load)
  useEffect(() => {
    loadRef.current = load
  })
  // One slot per hook, holding the last successful read together with the retainKey it
  // belongs to. Fencing by that key, rather than trusting whatever landed last, is what
  // keeps an abandoned read from painting the artifact the reader just left.
  // Failure is fenced by the same key plus the attempt that produced it: a rejection only
  // becomes visible when its own read was still current when it landed, so a superseded
  // read reports nothing at all, and asking for a retry retires the previous failure even
  // before the replacement read answers.
  const [state, setState] = useState<{
    retainKey: string
    attempt: number
    artifactVersion: number
    artifact: T | null
    failed: boolean
  }>({
    retainKey,
    attempt: 0,
    artifactVersion: 0,
    artifact: null,
    failed: false
  })
  const [attempt, setAttempt] = useState(0)
  const attemptRef = useRef(0)
  useEffect(() => {
    if (!enabled) return undefined
    let active = true
    const controller = new AbortController()
    void loadRef
      .current(controller.signal)
      .then((value) => {
        if (!active || controller.signal.aborted) return
        setState((previous) => ({
          retainKey,
          attempt,
          artifactVersion: previous.artifactVersion + 1,
          artifact: value,
          failed: false
        }))
      })
      .catch(() => {
        if (!active || controller.signal.aborted) return
        setState((prev) =>
          prev.retainKey === retainKey && prev.artifact !== null
            ? prev
            : {
                retainKey,
                attempt,
                artifactVersion: prev.artifactVersion,
                artifact: null,
                failed: true
              }
        )
      })
    return () => {
      active = false
      controller.abort()
    }
  }, [enabled, identity, retainKey, attempt])
  const reload = useCallback(() => {
    const next = attemptRef.current + 1
    attemptRef.current = next
    setAttempt(next)
    return next
  }, [])
  return {
    artifact: state.retainKey === retainKey ? state.artifact : null,
    artifactAttempt: state.attempt,
    artifactVersion: state.artifactVersion,
    failed: state.retainKey === retainKey && state.attempt === attempt && state.failed,
    reload
  }
}
